/*
  VRG-695 — the pure half of "Page titles & link previews": joining a sitemap route to the
  analytics and Search Console figures for the same page, sorting, and the deep link the
  Analytics → Website → Pages table uses. No imports, so the guard spec
  (`test/seo-page-meta-model.guard.spec.ts`) loads it directly.
*/

/**
 * One key for "the same page" across three sources that spell it differently:
 *   sitemap   `https://vera.inc/tags`          (absolute, no trailing slash)
 *   Search    `https://www.vera.inc/tags/?x=1` (absolute; host, slash and query vary)
 *   analytics `/tags`                          (a path, sometimes with `?utm_…` or `#…`)
 * → `/tags`. Host, query and hash are dropped, trailing slashes collapsed, and the path is
 * lower-cased because the analytics API already lower-cases its paths (WebsitePageRow.path);
 * every vera.inc route is lower-case, so nothing distinct is merged.
 */
export const normalizePath = (raw: string): string => {
  let path = raw.trim()
  const scheme = /^[a-z][a-z0-9+.-]*:\/\/[^/?#]*/i.exec(path)
  if (scheme) path = path.slice(scheme[0].length)
  path = path.split('#')[0].split('?')[0]
  if (!path.startsWith('/')) path = `/${path}`
  path = path.replace(/\/{2,}/g, '/').replace(/\/+$/, '')
  return path === '' ? '/' : path.toLowerCase()
}

export interface SearchTotals {
  clicks: number
  impressions: number
  /** clicks ÷ impressions; null with no impressions. */
  ctr: number | null
  /** Impression-weighted average position; null with no impressions. */
  position: number | null
}

/**
 * Search Console page rows → totals per normalised path. Two spellings of one page (www and
 * bare host, a trailing slash, a query string) are summed, and position is re-weighted by
 * impressions, the same rollup Search Console itself uses.
 */
export const searchByPath = (
  rows: ReadonlyArray<{ key: string; clicks: number; impressions: number; position: number | null }>,
): Map<string, SearchTotals> => {
  const acc = new Map<string, { clicks: number; impressions: number; wpos: number; wimp: number }>()
  for (const r of rows) {
    const path = normalizePath(r.key)
    const cur = acc.get(path) ?? { clicks: 0, impressions: 0, wpos: 0, wimp: 0 }
    cur.clicks += r.clicks
    cur.impressions += r.impressions
    if (r.position !== null && r.impressions > 0) {
      cur.wpos += r.position * r.impressions
      cur.wimp += r.impressions
    }
    acc.set(path, cur)
  }
  const out = new Map<string, SearchTotals>()
  for (const [path, a] of acc)
    out.set(path, {
      clicks: a.clicks,
      impressions: a.impressions,
      ctr: a.impressions > 0 ? a.clicks / a.impressions : null,
      position: a.wimp > 0 ? a.wpos / a.wimp : null,
    })
  return out
}

export interface VisitTotals {
  visitors: number | null
  pageviews: number | null
}

/** Analytics page rows → visitors and page views per normalised path (summed). */
export const visitsByPath = (
  rows: ReadonlyArray<{ path: string; visitors: number | null; pageviews: number | null }>,
): Map<string, VisitTotals> => {
  const out = new Map<string, VisitTotals>()
  const add = (a: number | null, b: number | null) => (a === null && b === null ? null : (a ?? 0) + (b ?? 0))
  for (const r of rows) {
    if (r.path.startsWith('__')) continue // the analytics table's synthetic "Other paths" row
    const path = normalizePath(r.path)
    const cur = out.get(path) ?? { visitors: null, pageviews: null }
    out.set(path, { visitors: add(cur.visitors, r.visitors), pageviews: add(cur.pageviews, r.pageviews) })
  }
  return out
}

/**
 * "Impressions but low CTR": impressions that did not become a click (impressions − clicks).
 * Ranks pages people SEE in Google and pass over, which is where a better title earns the
 * most. Null with no Search Console row.
 */
export const unclickedImpressions = (s: SearchTotals | undefined): number | null =>
  s ? Math.max(0, s.impressions - s.clicks) : null

export type SortKey =
  | 'route'
  | 'visitors'
  | 'impressions'
  | 'clicks'
  | 'ctr'
  | 'position'
  | 'unclicked'

/**
 * Sort rows by one key. Route ascends; every figure descends except position, where 1 is
 * best and so ascends. A page with no figure always sorts last, in either direction, so a
 * missing value never reads as the top or bottom of a ranking.
 */
export const sortByKey = <T extends { route: string }>(
  rows: readonly T[],
  key: SortKey,
  value: (row: T, key: Exclude<SortKey, 'route'>) => number | null,
): T[] => {
  const copy = [...rows]
  if (key === 'route') return copy.sort((a, b) => (a.route < b.route ? -1 : a.route > b.route ? 1 : 0))
  const asc = key === 'position'
  return copy.sort((a, b) => {
    const va = value(a, key)
    const vb = value(b, key)
    if (va === null && vb === null) return a.route < b.route ? -1 : 1
    if (va === null) return 1
    if (vb === null) return -1
    return (asc ? va - vb : vb - va) || (a.route < b.route ? -1 : 1)
  })
}

/** The SEO page's section for this view, and the param that lands on one route. */
export const PAGE_META_SECTION = 'page-meta'
export const PAGE_META_ROUTE_PARAM = 'route'

/** `/internal/gtm/seo?seo=page-meta&route=%2Fpricing` — opens the view on that row. */
export const pageMetaDeepLink = (seoPath: string, route: string): string =>
  `${seoPath}?seo=${PAGE_META_SECTION}&${PAGE_META_ROUTE_PARAM}=${encodeURIComponent(normalizePath(route))}`

/** Where search copy is edited. vera-website is the only home for it (VRG-695). */
export const META_SOURCE_URL = 'https://github.com/verainc/vera-website/blob/main/content/meta.ts'
export const SHARE_SOURCE_URL = 'https://github.com/verainc/vera-website/blob/main/content/share.ts'
